import React from 'react';
import { type ReactNode } from 'react';
import ResultsHeader from './ResultsHeader';
import PromptEditor from './PromptEditor';
import PromptDisplay from './PromptDisplay';
import AIChat from './AIChat';
import ResultsFooter from './ResultsFooter';
import { useToast } from '@/hooks/use-toast';
import { User } from '@/types/firebase';

interface ResultsLayoutProps {
  children?: ReactNode;
  projectDescription: string;
  setProjectDescription: (value: string) => void;
  additionalDescription: string;
  setAdditionalDescription: (value: string) => void;
  generatedPrompts: string[];
  isGenerating: boolean;
  chatMessages: {type: 'user' | 'ai'; content: string}[];
  setChatMessages: React.Dispatch<React.SetStateAction<{type: 'user' | 'ai'; content: string}[]>>;
  showTypingEffect: boolean;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  onRegeneratePrompt: (index: number) => void;
  onRegenerate: (
    projectDescription: string, 
    additionalDescription: string, 
    setIsGenerating: (value: boolean) => void,
    setShowTypingEffect: (value: boolean) => void,
    setChatMessages: (updater: ((prev: any[]) => any[]) | any[]) => void,
    setGeneratedPrompts: (prompts: string[]) => void
  ) => void;
  currentUser: User | null;
  setIsGenerating: (value: boolean) => void;
  setShowTypingEffect: (value: boolean) => void;
  setGeneratedPrompts: (prompts: string[]) => void;
}

export const ResultsLayout: React.FC<ResultsLayoutProps> = ({ 
  children,
  projectDescription,
  setProjectDescription,
  additionalDescription,
  setAdditionalDescription,
  generatedPrompts,
  isGenerating,
  chatMessages,
  setChatMessages,
  showTypingEffect,
  isFullscreen,
  toggleFullscreen,
  onRegeneratePrompt,
  onRegenerate,
  currentUser,
  setIsGenerating,
  setShowTypingEffect,
  setGeneratedPrompts
}) => {
  const { toast } = useToast();

  const copyAllPrompts = () => {
    if (!generatedPrompts.length) return;
    
    const allPromptsText = generatedPrompts.join('\n\n---\n\n');
    navigator.clipboard.writeText(allPromptsText);
    
    toast({
      title: "Copied to clipboard",
      description: "All prompts have been copied to your clipboard",
    });
  };

  const saveSession = () => {
    toast({
      title: "Session Saved",
      description: "Your prompts have been saved for future reference",
    });
  };

  const handleRegeneratePrompts = () => {
    onRegenerate(
      projectDescription,
      additionalDescription,
      setIsGenerating,
      setShowTypingEffect,
      setChatMessages,
      setGeneratedPrompts
    );
  };

  const handleRegenerateSpecificPrompt = (index: number) => {
    onRegeneratePrompt(
      index,
      projectDescription,
      additionalDescription,
      generatedPrompts,
      setGeneratedPrompts,
      setChatMessages,
      setIsGenerating
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
      <ResultsHeader 
        copyAllPrompts={copyAllPrompts}
        hasPrompts={generatedPrompts.length > 0}
        saveSession={saveSession}
        toggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
      />
      
      <main className="flex-grow">
        <div className="container mx-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">
            <div className="lg:col-span-5 space-y-6">
              <PromptEditor 
                projectDescription={projectDescription}
                setProjectDescription={setProjectDescription}
                additionalDescription={additionalDescription}
                setAdditionalDescription={setAdditionalDescription}
                isGenerating={isGenerating}
                onRegenerate={handleRegeneratePrompts}
                hasPrompts={generatedPrompts.length > 0}
              />
              
              <AIChat 
                chatMessages={chatMessages}
                setChatMessages={setChatMessages}
                showTypingEffect={showTypingEffect}
              />
            </div>
            
            <div className="lg:col-span-7">
              <PromptDisplay 
                prompts={generatedPrompts}
                isGenerating={isGenerating}
                onRegeneratePrompt={handleRegenerateSpecificPrompt}
                onCopyAllPrompts={copyAllPrompts}
              />
            </div>
          </div>
        </div>
      </main>
      
      <ResultsFooter />
      
      {children}
    </div>
  );
};
